import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, ErrorNote, Field, InfoNote, inputClass } from '../components/ui';
import { useSession } from '../lib/session';
import greensynLockup from '../assets/greensyn-logo-lockup.svg';

export function LoginPage() {
  const {
    login,
    loginError,
    isLoggingIn,
    isAuthenticated,
    isLoading,
    mustChangePassword,
    authNotice,
  } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // The session-carried notice survives the redirect after a password change;
  // router state is a fallback for any other caller that sets one.
  const notice =
    authNotice ?? (location.state as { notice?: string } | null)?.notice ?? null;

  if (!isLoading && isAuthenticated) {
    return <Navigate to={mustChangePassword ? '/profile' : '/'} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const result = await login(email.trim(), password);
      // A temporary password lands the person straight on the page that can
      // replace it, rather than a dashboard whose API calls would all be refused.
      navigate(result.user.mustChangePassword ? '/profile' : '/', { replace: true });
    } catch {
      // loginError from the session context renders below.
    }
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className="gs-logo-line" />
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center text-center">
            <img
              src={greensynLockup}
              alt="GreenSyn Life Science"
              className="h-auto w-full max-w-[20rem]"
            />
            <h1 className="mt-3 text-2xl font-bold text-ink">Login to Continue</h1>
            <p className="mt-2 text-sm font-light text-ink-body">
              Restricted Page - Please sign in to continue.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded-lg border border-surface-muted bg-white p-6 shadow-sm"
          >
            {notice ? <InfoNote>{notice}</InfoNote> : null}

            <Field label="Email">
              <input
                className={inputClass}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </Field>

            <Field label="Password">
              <input
                className={inputClass}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </Field>

            {loginError ? <ErrorNote>{loginError}</ErrorNote> : null}

            <Button type="submit" disabled={isLoggingIn}>
              {isLoggingIn ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
